// @ts-nocheck
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../../supabaseClient';
import type { Session } from '@supabase/supabase-js';
import type { Profile } from '../../types';
import type {
  LegalSource,
  LegalDocumentPreview,
  LegalUpdateEntry,
  NotificationTopicRecord,
  NotificationPreferenceRow,
} from '../../types/admin';
import { format, formatDistanceToNow } from 'date-fns';
import { useAdminToast } from './useAdminToast';

type LegalRadarTab = 'overview' | 'sources' | 'documents' | 'updates' | 'preferences';

interface AdminLegalRadarProps {
  session: Session;
  profile: Profile | null;
}

const tabConfig: Array<{ id: LegalRadarTab; label: string; icon: string }> = [
  { id: 'overview', label: 'Overview', icon: 'space_dashboard' },
  { id: 'sources', label: 'Sources', icon: 'public' },
  { id: 'documents', label: 'Documents', icon: 'description' },
  { id: 'updates', label: 'Summaries', icon: 'bolt' },
  { id: 'preferences', label: 'Notifications', icon: 'notifications' },
];

const defaultSourceForm = {
  name: '',
  url: '',
  base_url: '',
  source_type: 'rss_feed',
  jurisdiction: 'DE',
  license_type: '',
  license_url: '',
  terms_of_use_url: '',
  update_freq: 'weekly',
  scrape_allowed: true,
  notes: '',
};

const defaultTopicForm = {
  key: '',
  label: '',
  description: '',
  is_active: true,
};

const statusBadge: Record<string, string> = {
  draft: 'bg-amber-100/80 text-amber-900 dark:bg-amber-900/30 dark:text-amber-100',
  approved: 'bg-blue-100/80 text-blue-900 dark:bg-blue-900/30 dark:text-blue-100',
  published: 'bg-emerald-100/80 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-100',
};

const riskBadge: Record<string, string> = {
  info: 'bg-slate-100 text-slate-800 dark:bg-slate-900/40 dark:text-slate-100',
  important: 'bg-orange-100 text-orange-900 dark:bg-orange-900/30 dark:text-orange-100',
  critical: 'bg-rose-100 text-rose-900 dark:bg-rose-900/30 dark:text-rose-100',
};

export const AdminLegalRadar: React.FC<AdminLegalRadarProps> = () => {
  const [activeTab, setActiveTab] = useState<LegalRadarTab>('overview');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sources, setSources] = useState<LegalSource[]>([]);
  const [documents, setDocuments] = useState<LegalDocumentPreview[]>([]);
  const [legalUpdates, setLegalUpdates] = useState<LegalUpdateEntry[]>([]);
  const [notificationTopics, setNotificationTopics] = useState<NotificationTopicRecord[]>([]);
  const [preferences, setPreferences] = useState<NotificationPreferenceRow[]>([]);
  const [consentLog, setConsentLog] = useState<any[]>([]);
  const [stats, setStats] = useState({
    totalSources: 0,
    activeSources: 0,
    totalDocuments: 0,
    totalUpdates: 0,
    publishedUpdates: 0,
    consentEvents30d: 0,
  });
  const [sourceForm, setSourceForm] = useState(defaultSourceForm);
  const [editingSource, setEditingSource] = useState<LegalSource | null>(null);
  const [savingSource, setSavingSource] = useState(false);
  const [topicForm, setTopicForm] = useState(defaultTopicForm);
  const [editingTopic, setEditingTopic] = useState<NotificationTopicRecord | null>(null);
  const [savingTopic, setSavingTopic] = useState(false);
  const [functionRunning, setFunctionRunning] = useState<string | null>(null);
  const cronSecretRef = useRef(
    (import.meta as any)?.env?.VITE_LEGAL_RADAR_CRON_SECRET ??
      (typeof process !== 'undefined' ? process.env.VITE_LEGAL_RADAR_CRON_SECRET : '') ??
      '',
  );
  const warnedAboutMissingSecret = useRef(false);
  const { showError, showSuccess } = useAdminToast();

  useEffect(() => {
    loadAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAllData = async () => {
    try {
      if (!refreshing) {
        setLoading(true);
      }
      await Promise.all([
        fetchSources(),
        fetchDocuments(),
        fetchUpdates(),
        fetchNotificationTopics(),
        fetchPreferences(),
        fetchConsentLog(),
        fetchStats(),
      ]);
    } catch (err) {
      console.error('[AdminLegalRadar] Failed to load data', err);
      showError('Failed to load Legal Radar data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchSources = async () => {
    const { data, error } = await supabase.from('legal_sources').select('*').order('name', { ascending: true });
    if (error) throw error;
    setSources(data || []);
  };

  const fetchDocuments = async () => {
    const { data, error } = await supabase
      .from('legal_documents')
      .select('id,title,url,jurisdiction,decision_date,created_at,topics,source_id,legal_sources(name)')
      .order('created_at', { ascending: false })
      .limit(25);

    if (error) throw error;

    const docs = data || [];
    const docIds = docs.map((doc) => doc.id);
    let summaryMap: Record<string, { id: string; status: string; published_at: string | null }> = {};

    if (docIds.length > 0) {
      const { data: summaries, error: summaryError } = await supabase
        .from('legal_updates')
        .select('id, document_id, status, published_at')
        .in('document_id', docIds);
      if (summaryError) throw summaryError;
      summaryMap = (summaries || []).reduce((acc, row) => {
        acc[row.document_id] = { id: row.id, status: row.status, published_at: row.published_at };
        return acc;
      }, {} as Record<string, { id: string; status: string; published_at: string | null }>);
    }

    setDocuments(
      docs.map((doc: any) => ({
        id: doc.id,
        title: doc.title,
        url: doc.url,
        jurisdiction: doc.jurisdiction,
        decision_date: doc.decision_date,
        created_at: doc.created_at,
        topics: doc.topics || [],
        source_id: doc.source_id,
        source_name: doc.legal_sources?.name || 'Unknown',
        summary_status: summaryMap[doc.id]?.status || null,
        summary_id: summaryMap[doc.id]?.id || null,
        summary_published: summaryMap[doc.id]?.published_at || null,
      })),
    );
  };

  const fetchUpdates = async () => {
    const { data, error } = await supabase
      .from('legal_updates')
      .select('id, document_id, summary_plain, impact_non_personal, risk_level, status, created_at, published_at')
      .order('created_at', { ascending: false })
      .limit(50);
    if (error) throw error;

    const updates = data || [];
    const docIds = Array.from(new Set(updates.map((u) => u.document_id)));
    let docMap: Record<string, { title: string; jurisdiction: string | null; decision_date: string | null }> = {};

    if (docIds.length > 0) {
      const { data: docInfo, error: docError } = await supabase
        .from('legal_documents')
        .select('id,title,jurisdiction,decision_date')
        .in('id', docIds);
      if (docError) throw docError;
      docMap = (docInfo || []).reduce((acc, doc) => {
        acc[doc.id] = {
          title: doc.title,
          jurisdiction: doc.jurisdiction,
          decision_date: doc.decision_date,
        };
        return acc;
      }, {} as Record<string, { title: string; jurisdiction: string | null; decision_date: string | null }>);
    }

    setLegalUpdates(
      updates.map((entry) => ({
        ...entry,
        document_title: docMap[entry.document_id]?.title || 'Untitled document',
        jurisdiction: docMap[entry.document_id]?.jurisdiction || null,
        decision_date: docMap[entry.document_id]?.decision_date || null,
      })),
    );
  };

  const fetchNotificationTopics = async () => {
    const { data, error } = await supabase
      .from('notification_topics')
      .select('*')
      .order('label', { ascending: true });
    if (error) throw error;
    setNotificationTopics(data || []);
  };

  const fetchPreferences = async () => {
    const { data, error } = await supabase
      .from('user_notification_settings')
      .select('topic_id, channel, enabled')
      .eq('enabled', true);
    if (error) throw error;
    setPreferences(data || []);
  };

  const fetchConsentLog = async () => {
    const { data, error } = await supabase
      .from('consent_log')
      .select('id, event_type, created_at, user_id')
      .order('created_at', { ascending: false })
      .limit(10);
    if (error) throw error;
    setConsentLog(data || []);
  };

  const fetchStats = async () => {
    const thirtyDaysAgo = new Date(Date.now() - 1000 * 60 * 60 * 24 * 30).toISOString();
    const [{ count: totalSources }, { count: activeSources }, { count: totalDocuments }, { count: totalUpdates }, { count: publishedUpdates }, { count: consentEvents30d }] =
      await Promise.all([
        supabase.from('legal_sources').select('id', { count: 'exact', head: true }),
        supabase.from('legal_sources').select('id', { count: 'exact', head: true }).eq('is_active', true),
        supabase.from('legal_documents').select('id', { count: 'exact', head: true }),
        supabase.from('legal_updates').select('id', { count: 'exact', head: true }),
        supabase.from('legal_updates').select('id', { count: 'exact', head: true }).eq('status', 'published'),
        supabase.from('consent_log').select('id', { count: 'exact', head: true }).gte('created_at', thirtyDaysAgo),
      ]);

    setStats({
      totalSources: totalSources || 0,
      activeSources: activeSources || 0,
      totalDocuments: totalDocuments || 0,
      totalUpdates: totalUpdates || 0,
      publishedUpdates: publishedUpdates || 0,
      consentEvents30d: consentEvents30d || 0,
    });
  };

  const refreshAll = () => {
    setRefreshing(true);
    loadAllData();
  };

  const startEditSource = (source: LegalSource) => {
    setEditingSource(source);
    setSourceForm({
      name: source.name,
      url: source.url,
      base_url: source.base_url || source.url,
      source_type: source.source_type || 'rss_feed',
      jurisdiction: source.jurisdiction || 'DE',
      license_type: source.license_type || '',
      license_url: source.license_url || '',
      terms_of_use_url: source.terms_of_use_url || '',
      update_freq: source.update_freq || source.update_frequency || 'weekly',
      scrape_allowed: source.scrape_allowed ?? true,
      notes: source.notes || '',
    });
  };

  const resetSourceForm = () => {
    setEditingSource(null);
    setSourceForm(defaultSourceForm);
  };

  const handleSourceSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingSource(true);
    try {
      const payload: any = {
        ...sourceForm,
        base_url: sourceForm.base_url || sourceForm.url,
        update_freq: sourceForm.update_freq || 'weekly',
        update_frequency: sourceForm.update_freq || 'weekly',
        license_info:
          sourceForm.license_type && sourceForm.license_type.trim().length > 0
            ? `License: ${sourceForm.license_type}`
            : null,
      };

      if (editingSource) {
        const { error } = await supabase.from('legal_sources').update(payload).eq('id', editingSource.id);
        if (error) throw error;
        showSuccess('Source updated');
      } else {
        const { error } = await supabase.from('legal_sources').insert(payload);
        if (error) throw error;
        showSuccess('Source created');
      }
      await fetchSources();
      resetSourceForm();
    } catch (err: any) {
      console.error('[AdminLegalRadar] Failed to save source', err);
      showError(err.message || 'Failed to save source');
    } finally {
      setSavingSource(false);
    }
  };

  const toggleSourceField = async (source: LegalSource, field: 'is_active' | 'scrape_allowed') => {
    try {
      const updatedValue = !source[field];
      const { error } = await supabase.from('legal_sources').update({ [field]: updatedValue }).eq('id', source.id);
      if (error) throw error;
      showSuccess(`${field === 'is_active' ? 'Source status' : 'Scrape flag'} updated`);
      await fetchSources();
    } catch (err: any) {
      console.error('[AdminLegalRadar] Failed to toggle source field', err);
      showError(err.message || 'Failed to update source');
    }
  };

  const invokeEdgeFunction = async (fn: 'ingest-legal-sources' | 'generate-legal-updates') => {
    setFunctionRunning(fn);
    try {
      const headers: Record<string, string> = {};
      if (cronSecretRef.current) {
        headers.Authorization = `Bearer ${cronSecretRef.current}`;
      } else if (!warnedAboutMissingSecret.current) {
        warnedAboutMissingSecret.current = true;
        showError('Set VITE_LEGAL_RADAR_CRON_SECRET to call Legal Radar functions securely.');
      }

      const { error } = await supabase.functions.invoke(fn, {
        body: {},
        headers,
      });
      if (error) throw error;
      showSuccess(fn === 'ingest-legal-sources' ? 'Ingestion function triggered' : 'AI summaries triggered');
      refreshAll();
    } catch (err: any) {
      console.error('[AdminLegalRadar] Edge function failed', err);
      showError(err.message || 'Unable to run serverless function');
    } finally {
      setFunctionRunning(null);
    }
  };

  const handleUpdateStatus = async (updateId: string, status: 'draft' | 'approved' | 'published') => {
    try {
      const payload: any = { status };
      if (status === 'published') {
        payload.published_at = new Date().toISOString();
      }
      const { error } = await supabase.from('legal_updates').update(payload).eq('id', updateId);
      if (error) throw error;
      showSuccess('Update status changed');
      await Promise.all([fetchDocuments(), fetchUpdates(), fetchStats()]);
    } catch (err: any) {
      console.error('[AdminLegalRadar] Failed to change update status', err);
      showError(err.message || 'Failed to update status');
    }
  };

  const startEditTopic = (topic: NotificationTopicRecord) => {
    setEditingTopic(topic);
    setTopicForm({
      key: topic.key,
      label: topic.label,
      description: topic.description || '',
      is_active: topic.is_active,
    });
  };

  const resetTopicForm = () => {
    setEditingTopic(null);
    setTopicForm(defaultTopicForm);
  };

  const handleTopicSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingTopic(true);
    try {
      const payload = {
        key: topicForm.key.trim(),
        label: topicForm.label.trim(),
        description: topicForm.description?.trim() || null,
        is_active: topicForm.is_active,
      };
      if (!payload.key || !payload.label) {
        throw new Error('Key and label are required');
      }

      if (editingTopic) {
        const { error } = await supabase.from('notification_topics').update(payload).eq('id', editingTopic.id);
        if (error) throw error;
        showSuccess('Notification topic updated');
      } else {
        const { error } = await supabase.from('notification_topics').insert(payload);
        if (error) throw error;
        showSuccess('Notification topic created');
      }
      await fetchNotificationTopics();
      resetTopicForm();
    } catch (err: any) {
      console.error('[AdminLegalRadar] Failed to save topic', err);
      showError(err.message || 'Failed to save notification topic');
    } finally {
      setSavingTopic(false);
    }
  };

  const toggleTopicActive = async (topic: NotificationTopicRecord) => {
    try {
      const { error } = await supabase
        .from('notification_topics')
        .update({ is_active: !topic.is_active })
        .eq('id', topic.id);
      if (error) throw error;
      showSuccess('Topic visibility updated');
      await fetchNotificationTopics();
    } catch (err: any) {
      console.error('[AdminLegalRadar] Failed to toggle topic', err);
      showError(err.message || 'Failed to toggle topic');
    }
  };

  const topicPreferenceStats = useMemo(() => {
    const lookup = notificationTopics.reduce((acc, topic) => {
      acc[topic.id] = {
        topic,
        email: 0,
        in_app: 0,
      };
      return acc;
    }, {} as Record<string, { topic: NotificationTopicRecord; email: number; in_app: number }>);

    (preferences || []).forEach((pref) => {
      if (!pref.enabled) return;
      const entry = lookup[pref.topic_id];
      if (entry) {
        if (pref.channel === 'email') {
          entry.email += 1;
        } else {
          entry.in_app += 1;
        }
      }
    });

    return Object.values(lookup);
  }, [notificationTopics, preferences]);

  const documentsNeedingSummary = useMemo(
    () => documents.filter((doc) => !doc.summary_status || doc.summary_status === 'draft').length,
    [documents],
  );

  const pendingApproval = useMemo(
    () => legalUpdates.filter((update) => update.status === 'approved').length,
    [legalUpdates],
  );

  const renderOverview = () => (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-2xl border border-luna-secondary/20 bg-white dark:bg-luna-primary/40 p-4 shadow-sm">
          <p className="text-xs uppercase tracking-wider text-luna-text-light/60 dark:text-luna-text-dark/60 mb-2">
            Active Sources
          </p>
          <div className="flex items-baseline justify-between">
            <h3 className="text-3xl font-black text-luna-text-light dark:text-white">{stats.activeSources}</h3>
            <span className="text-sm text-luna-text-light/60 dark:text-luna-text-dark/60">
              of {stats.totalSources}
            </span>
          </div>
          <p className="text-xs text-luna-text-light/60 dark:text-luna-text-dark/60 mt-2">
            {sources.filter((source) => source.scrape_allowed).length} allow automated ingestion
          </p>
        </div>
        <div className="rounded-2xl border border-luna-secondary/20 bg-white dark:bg-luna-primary/40 p-4 shadow-sm">
          <p className="text-xs uppercase tracking-wider text-luna-text-light/60 dark:text-luna-text-dark/60 mb-2">
            Published Summaries
          </p>
          <div className="flex items-baseline justify-between">
            <h3 className="text-3xl font-black text-luna-text-light dark:text-white">{stats.publishedUpdates}</h3>
            <span className="text-sm text-luna-text-light/60 dark:text-luna-text-dark/60">
              of {stats.totalUpdates}
            </span>
          </div>
          <p className="text-xs text-luna-text-light/60 dark:text-luna-text-dark/60 mt-2">
            {pendingApproval} summaries waiting for approval
          </p>
        </div>
        <div className="rounded-2xl border border-luna-secondary/20 bg-white dark:bg-luna-primary/40 p-4 shadow-sm">
          <p className="text-xs uppercase tracking-wider text-luna-text-light/60 dark:text-luna-text-dark/60 mb-2">
            Docs needing summary
          </p>
          <div className="flex items-baseline justify-between">
            <h3 className="text-3xl font-black text-luna-text-light dark:text-white">{documentsNeedingSummary}</h3>
            <span className="text-sm text-luna-text-light/60 dark:text-luna-text-dark/60">
              of {documents.length} shown
            </span>
          </div>
          <p className="text-xs text-luna-text-light/60 dark:text-luna-text-dark/60 mt-2">
            Recent ingestion queue
          </p>
        </div>
        <div className="rounded-2xl border border-luna-secondary/20 bg-white dark:bg-luna-primary/40 p-4 shadow-sm">
          <p className="text-xs uppercase tracking-wider text-luna-text-light/60 dark:text-luna-text-dark/60 mb-2">
            Consent events (30d)
          </p>
          <div className="flex items-baseline justify-between">
            <h3 className="text-3xl font-black text-luna-text-light dark:text-white">{stats.consentEvents30d}</h3>
            <span className="text-sm text-luna-text-light/60 dark:text-luna-text-dark/60">events</span>
          </div>
          <p className="text-xs text-luna-text-light/60 dark:text-luna-text-dark/60 mt-2">
            {preferences.length} active notification opt-ins
          </p>
        </div>
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-luna-secondary/20 bg-white dark:bg-luna-primary/40 p-4 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold text-luna-text-light dark:text-white">Latest ingestion</h3>
              <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                {sources.slice(0, 3).map((source) => source.name).join(', ') || 'No sources yet'}
              </p>
            </div>
            <button
              onClick={() => invokeEdgeFunction('ingest-legal-sources')}
              className="inline-flex items-center gap-2 rounded-full border border-luna-secondary/40 px-4 py-2 text-xs font-semibold text-luna-secondary hover:border-luna-secondary"
              disabled={functionRunning === 'ingest-legal-sources'}
            >
              <span className="material-symbols-outlined text-sm">
                {functionRunning === 'ingest-legal-sources' ? 'hourglass' : 'sync'}
              </span>
              {functionRunning === 'ingest-legal-sources' ? 'Running…' : 'Run ingest'}
            </button>
          </div>
          <ul className="divide-y divide-luna-secondary/20 text-sm text-luna-text-light dark:text-luna-text-dark">
            {documents.slice(0, 6).map((doc) => (
              <li key={doc.id} className="py-3 flex items-center justify-between gap-4">
                <div>
                  <p className="font-semibold line-clamp-1">{doc.title}</p>
                  <p className="text-xs text-luna-text-light/60 dark:text-luna-text-dark/60">
                    {doc.source_name} · {doc.jurisdiction || 'N/A'}
                  </p>
                </div>
                <span
                  className={`text-xs font-semibold px-3 py-1 rounded-full ${
                    doc.summary_status ? statusBadge[doc.summary_status] || statusBadge.draft : 'bg-slate-100 text-slate-700'
                  }`}
                >
                  {doc.summary_status ? doc.summary_status : 'Pending'}
                </span>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-2xl border border-luna-secondary/20 bg-white dark:bg-luna-primary/40 p-4 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold text-luna-text-light dark:text-white">Notification reach</h3>
              <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                {topicPreferenceStats.reduce((sum, entry) => sum + entry.email + entry.in_app, 0)} active subscriptions
              </p>
            </div>
            <button
              onClick={() => invokeEdgeFunction('generate-legal-updates')}
              className="inline-flex items-center gap-2 rounded-full border border-luna-secondary/40 px-4 py-2 text-xs font-semibold text-luna-secondary hover:border-luna-secondary"
              disabled={functionRunning === 'generate-legal-updates'}
            >
              <span className="material-symbols-outlined text-sm">
                {functionRunning === 'generate-legal-updates' ? 'hourglass' : 'spark'}
              </span>
              {functionRunning === 'generate-legal-updates' ? 'Running…' : 'Run AI summaries'}
            </button>
          </div>
          <ul className="divide-y divide-luna-secondary/20 text-sm text-luna-text-light dark:text-luna-text-dark">
            {topicPreferenceStats.slice(0, 6).map((entry) => (
              <li key={entry.topic.id} className="py-3 flex items-center justify-between gap-4">
                <div>
                  <p className="font-semibold">{entry.topic.label}</p>
                  <p className="text-xs text-luna-text-light/60 dark:text-luna-text-dark/60">
                    {entry.topic.description || entry.topic.key}
                  </p>
                </div>
                <div className="text-right text-xs">
                  <div className="font-semibold">{entry.email} email</div>
                  <div className="text-luna-text-light/60 dark:text-luna-text-dark/60">{entry.in_app} in-app</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );

  const renderSources = () => (
    <div className="grid gap-6 lg:grid-cols-3">
      <div className="lg:col-span-2 rounded-2xl border border-luna-secondary/20 bg-white dark:bg-luna-primary/40 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-luna-secondary/20 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-luna-text-light dark:text-white">Source registry</h3>
            <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
              Manage ingestion endpoints and license metadata.
            </p>
          </div>
          <button
            onClick={refreshAll}
            className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-luna-secondary/40 text-luna-text-light dark:text-luna-text-dark"
          >
            <span className="material-symbols-outlined text-sm">refresh</span>
            Refresh
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-luna-text-light/60 dark:text-luna-text-dark/50">
                <th className="px-4 py-2">Source</th>
                <th className="px-4 py-2">Jurisdiction</th>
                <th className="px-4 py-2">License</th>
                <th className="px-4 py-2">Last crawl</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-luna-secondary/10 text-luna-text-light dark:text-luna-text-dark">
              {sources.map((source) => (
                <tr key={source.id}>
                  <td className="px-4 py-3">
                    <p className="font-semibold">{source.name}</p>
                    <p className="text-xs text-luna-text-light/60 dark:text-luna-text-dark/60">
                      {source.source_type} · {source.update_freq || source.update_frequency || 'weekly'}
                    </p>
                  </td>
                  <td className="px-4 py-3">{source.jurisdiction}</td>
                  <td className="px-4 py-3">
                    <p className="text-xs">{source.license_type || 'N/A'}</p>
                    <p className="text-[11px] text-luna-text-light/50 dark:text-luna-text-dark/50">
                      {source.scrape_allowed ? 'Scraping allowed' : 'Manual only'}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {source.last_crawled
                      ? formatDistanceToNow(new Date(source.last_crawled), { addSuffix: true })
                      : 'Never'}
                  </td>
                  <td className="px-4 py-3 text-right space-x-1">
                    <button
                      className="inline-flex items-center px-3 py-1.5 text-xs rounded-full border border-luna-secondary/20 hover:border-luna-secondary"
                      onClick={() => startEditSource(source)}
                    >
                      Edit
                    </button>
                    <button
                      className={`inline-flex items-center px-3 py-1.5 text-xs rounded-full border ${
                        source.is_active ? 'border-emerald-500 text-emerald-600' : 'border-slate-400 text-slate-500'
                      }`}
                      onClick={() => toggleSourceField(source, 'is_active')}
                    >
                      {source.is_active ? 'Active' : 'Enable'}
                    </button>
                    <button
                      className={`inline-flex items-center px-3 py-1.5 text-xs rounded-full border ${
                        source.scrape_allowed ? 'border-blue-500 text-blue-600' : 'border-amber-500 text-amber-600'
                      }`}
                      onClick={() => toggleSourceField(source, 'scrape_allowed')}
                    >
                      {source.scrape_allowed ? 'Scrape' : 'Manual'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {sources.length === 0 && (
            <div className="p-6 text-center text-sm text-luna-text-light/60 dark:text-luna-text-dark/60">
              No sources yet. Add your first source using the form.
            </div>
          )}
        </div>
      </div>
      <div className="rounded-2xl border border-luna-secondary/20 bg-white dark:bg-luna-primary/40 shadow-sm p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-luna-text-light dark:text-white">
              {editingSource ? 'Edit Source' : 'Add Source'}
            </h3>
            <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
              Official, license-cleared data providers.
            </p>
          </div>
          {editingSource && (
            <button
              onClick={resetSourceForm}
              className="text-xs text-luna-secondary hover:underline"
            >
              Cancel
            </button>
          )}
        </div>
        <form className="space-y-4" onSubmit={handleSourceSubmit}>
          <div>
            <label htmlFor="source-name" className="text-xs font-semibold text-luna-text-light/70 dark:text-luna-text-dark/70">Name</label>
            <input
              id="source-name"
              type="text"
              className="w-full rounded-xl border border-luna-secondary/30 bg-transparent px-3 py-2 text-sm"
              value={sourceForm.name}
              onChange={(e) => setSourceForm({ ...sourceForm, name: e.target.value })}
              required
            />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label htmlFor="source-url" className="text-xs font-semibold text-luna-text-light/70 dark:text-luna-text-dark/70">Source URL</label>
              <input
                id="source-url"
                type="url"
                className="w-full rounded-xl border border-luna-secondary/30 bg-transparent px-3 py-2 text-sm"
                value={sourceForm.url}
                onChange={(e) => setSourceForm({ ...sourceForm, url: e.target.value })}
                required
              />
            </div>
            <div>
              <label htmlFor="base-url" className="text-xs font-semibold text-luna-text-light/70 dark:text-luna-text-dark/70">Base URL</label>
              <input
                id="base-url"
                type="url"
                className="w-full rounded-xl border border-luna-secondary/30 bg-transparent px-3 py-2 text-sm"
                value={sourceForm.base_url}
                onChange={(e) => setSourceForm({ ...sourceForm, base_url: e.target.value })}
                placeholder="Defaults to Source URL"
              />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label htmlFor="jurisdiction" className="text-xs font-semibold text-luna-text-light/70 dark:text-luna-text-dark/70">Jurisdiction</label>
              <select
                id="jurisdiction"
                value={sourceForm.jurisdiction}
                onChange={(e) => setSourceForm({ ...sourceForm, jurisdiction: e.target.value })}
                className="w-full rounded-xl border border-luna-secondary/30 bg-transparent px-3 py-2 text-sm"
              >
                <option value="DE">Germany</option>
                <option value="EU">European Union</option>
                <option value="DE+EU">DE + EU</option>
              </select>
            </div>
            <div>
              <label htmlFor="source-type" className="text-xs font-semibold text-luna-text-light/70 dark:text-luna-text-dark/70">Source type</label>
              <select
                id="source-type"
                value={sourceForm.source_type}
                onChange={(e) => setSourceForm({ ...sourceForm, source_type: e.target.value })}
                className="w-full rounded-xl border border-luna-secondary/30 bg-transparent px-3 py-2 text-sm"
              >
                <option value="rss_feed">RSS feed</option>
                <option value="api">API</option>
                <option value="scraper">Scraper</option>
                <option value="manual">Manual</option>
              </select>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label htmlFor="update-freq" className="text-xs font-semibold text-luna-text-light/70 dark:text-luna-text-dark/70">Update frequency</label>
              <select
                id="update-freq"
                value={sourceForm.update_freq}
                onChange={(e) => setSourceForm({ ...sourceForm, update_freq: e.target.value })}
                className="w-full rounded-xl border border-luna-secondary/30 bg-transparent px-3 py-2 text-sm"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="manual">Manual</option>
              </select>
            </div>
            <div>
              <label htmlFor="license-type" className="text-xs font-semibold text-luna-text-light/70 dark:text-luna-text-dark/70">License type</label>
              <input
                id="license-type"
                type="text"
                className="w-full rounded-xl border border-luna-secondary/30 bg-transparent px-3 py-2 text-sm"
                value={sourceForm.license_type}
                onChange={(e) => setSourceForm({ ...sourceForm, license_type: e.target.value })}
                placeholder="open_data, public_domain…"
              />
            </div>
          </div>
          <textarea
            id="notes"
            className="w-full rounded-xl border border-luna-secondary/30 bg-transparent px-3 py-2 text-sm"
            rows={3}
            placeholder="Notes"
            value={sourceForm.notes}
            onChange={(e) => setSourceForm({ ...sourceForm, notes: e.target.value })}
          />
          <label htmlFor="scrape-allowed" className="flex items-center gap-2 text-sm text-luna-text-light dark:text-luna-text-dark">
            <input
              id="scrape-allowed"
              type="checkbox"
              checked={sourceForm.scrape_allowed}
              onChange={(e) => setSourceForm({ ...sourceForm, scrape_allowed: e.target.checked })}
            />
            Allow scraping / automated ingestion
          </label>
          <div className="flex items-center gap-2">
            <button
              type="submit"
              className="flex-1 rounded-xl bg-luna-secondary px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
              disabled={savingSource}
            >
              {savingSource ? 'Saving…' : editingSource ? 'Update Source' : 'Create Source'}
            </button>
            {editingSource && (
              <button
                type="button"
                className="rounded-xl border border-luna-secondary/30 px-4 py-2 text-sm"
                onClick={resetSourceForm}
              >
                Reset
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );

  const renderDocuments = () => (
    <div className="rounded-2xl border border-luna-secondary/20 bg-white dark:bg-luna-primary/40 shadow-sm overflow-hidden">
      <div className="p-4 border-b border-luna-secondary/20 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-luna-text-light dark:text-white">Latest legal documents</h3>
          <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
            Showing the 25 most recent ingested documents.
          </p>
        </div>
        <button
          onClick={refreshAll}
          className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-luna-secondary/40 text-luna-text-light dark:text-luna-text-dark"
        >
          <span className="material-symbols-outlined text-sm">refresh</span>
          Refresh
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-luna-text-light/60 dark:text-luna-text-dark/50">
              <th className="px-4 py-2">Title</th>
              <th className="px-4 py-2">Jurisdiction</th>
              <th className="px-4 py-2">Decision date</th>
              <th className="px-4 py-2">Source</th>
              <th className="px-4 py-2 text-right">Summary</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-luna-secondary/10 text-luna-text-light dark:text-luna-text-dark">
            {documents.map((doc) => (
              <tr key={doc.id}>
                <td className="px-4 py-3">
                  <p className="font-semibold line-clamp-1">{doc.title}</p>
                  <p className="text-xs text-luna-text-light/60 dark:text-luna-text-dark/60">
                    {format(new Date(doc.created_at), 'dd MMM yyyy HH:mm')}
                  </p>
                </td>
                <td className="px-4 py-3">{doc.jurisdiction || 'N/A'}</td>
                <td className="px-4 py-3">{doc.decision_date ? format(new Date(doc.decision_date), 'dd MMM yyyy') : 'Pending'}</td>
                <td className="px-4 py-3">{doc.source_name}</td>
                <td className="px-4 py-3 text-right">
                  <span
                    className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${
                      doc.summary_status ? statusBadge[doc.summary_status] || statusBadge.draft : 'bg-slate-100 text-slate-700'
                    }`}
                  >
                    <span className="material-symbols-outlined text-xs">bolt</span>
                    {doc.summary_status ? doc.summary_status : 'Pending'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderUpdates = () => (
    <div className="rounded-2xl border border-luna-secondary/20 bg-white dark:bg-luna-primary/40 shadow-sm overflow-hidden">
      <div className="p-4 border-b border-luna-secondary/20 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-luna-text-light dark:text-white">AI-generated summaries</h3>
          <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
            Review and publish Legal Radar updates.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => invokeEdgeFunction('generate-legal-updates')}
            className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-luna-secondary/40 text-luna-text-light dark:text-luna-text-dark disabled:opacity-60"
            disabled={functionRunning === 'generate-legal-updates'}
          >
            <span className="material-symbols-outlined text-sm">bolt</span>
            {functionRunning === 'generate-legal-updates' ? 'Running…' : 'Generate'}
          </button>
          <button
            onClick={refreshAll}
            className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-luna-secondary/40 text-luna-text-light dark:text-luna-text-dark"
          >
            <span className="material-symbols-outlined text-sm">refresh</span>
            Refresh
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-luna-text-light/60 dark:text-luna-text-dark/50">
              <th className="px-4 py-2">Summary</th>
              <th className="px-4 py-2">Risk</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2">Decision date</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-luna-secondary/10 text-luna-text-light dark:text-luna-text-dark">
            {legalUpdates.map((update) => (
              <tr key={update.id}>
                <td className="px-4 py-3">
                  <p className="font-semibold line-clamp-1">{update.document_title}</p>
                  <p className="text-xs text-luna-text-light/60 dark:text-luna-text-dark/60 line-clamp-2">
                    {update.summary_plain}
                  </p>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${riskBadge[update.risk_level]}`}>
                    {update.risk_level}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${statusBadge[update.status]}`}>
                    {update.status}
                  </span>
                  {update.published_at && (
                    <p className="text-[11px] text-luna-text-light/60 dark:text-luna-text-dark/60">
                      {format(new Date(update.published_at), 'dd MMM yyyy')}
                    </p>
                  )}
                </td>
                <td className="px-4 py-3">
                  {update.decision_date ? format(new Date(update.decision_date), 'dd MMM yyyy') : 'N/A'}
                </td>
                <td className="px-4 py-3 text-right space-x-1">
                  {update.status !== 'draft' && (
                    <button
                      className="inline-flex items-center px-2.5 py-1 text-xs rounded-full border border-luna-secondary/30"
                      onClick={() => handleUpdateStatus(update.id, 'draft')}
                    >
                      Draft
                    </button>
                  )}
                  {update.status !== 'approved' && (
                    <button
                      className="inline-flex items-center px-2.5 py-1 text-xs rounded-full border border-blue-400 text-blue-600"
                      onClick={() => handleUpdateStatus(update.id, 'approved')}
                    >
                      Approve
                    </button>
                  )}
                  {update.status !== 'published' && (
                    <button
                      className="inline-flex items-center px-2.5 py-1 text-xs rounded-full border border-emerald-400 text-emerald-600"
                      onClick={() => handleUpdateStatus(update.id, 'published')}
                    >
                      Publish
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {legalUpdates.length === 0 && (
          <div className="p-6 text-center text-sm text-luna-text-light/60 dark:text-luna-text-dark/60">
            No summaries yet. Trigger the AI summarizer to generate draft updates.
          </div>
        )}
      </div>
    </div>
  );

  const renderPreferences = () => (
    <div className="grid gap-6 xl:grid-cols-3">
      <div className="xl:col-span-2 rounded-2xl border border-luna-secondary/20 bg-white dark:bg-luna-primary/40 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-luna-secondary/20 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-luna-text-light dark:text-white">Notification topics</h3>
            <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
              Manage public-facing Legal Radar categories.
            </p>
          </div>
          <button
            onClick={refreshAll}
            className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-luna-secondary/40 text-luna-text-light dark:text-luna-text-dark"
          >
            <span className="material-symbols-outlined text-sm">refresh</span>
            Refresh
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-luna-text-light/60 dark:text-luna-text-dark/50">
                <th className="px-4 py-2">Topic</th>
                <th className="px-4 py-2">Subscribers</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-luna-secondary/10 text-luna-text-light dark:text-luna-text-dark">
              {topicPreferenceStats.map((entry) => (
                <tr key={entry.topic.id}>
                  <td className="px-4 py-3">
                    <p className="font-semibold">{entry.topic.label}</p>
                    <p className="text-xs text-luna-text-light/60 dark:text-luna-text-dark/60">
                      {entry.topic.description || entry.topic.key}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <div>{entry.email} email</div>
                    <div className="text-luna-text-light/60 dark:text-luna-text-dark/60">{entry.in_app} in-app</div>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                        entry.topic.is_active
                          ? 'bg-emerald-100/80 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-100'
                          : 'bg-slate-100 text-slate-700'
                      }`}
                    >
                      {entry.topic.is_active ? 'Active' : 'Paused'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right space-x-1">
                    <button
                      className="inline-flex items-center px-3 py-1.5 text-xs rounded-full border border-luna-secondary/30"
                      onClick={() => startEditTopic(entry.topic)}
                    >
                      Edit
                    </button>
                    <button
                      className="inline-flex items-center px-3 py-1.5 text-xs rounded-full border border-luna-secondary/30"
                      onClick={() => toggleTopicActive(entry.topic)}
                    >
                      {entry.topic.is_active ? 'Disable' : 'Enable'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {topicPreferenceStats.length === 0 && (
            <div className="p-6 text-center text-sm text-luna-text-light/60 dark:text-luna-text-dark/60">
              No notification topics found. Add one using the form.
            </div>
          )}
        </div>
      </div>
      <div className="space-y-6">
        <div className="rounded-2xl border border-luna-secondary/20 bg-white dark:bg-luna-primary/40 shadow-sm p-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold text-luna-text-light dark:text-white">
                {editingTopic ? 'Edit topic' : 'Add topic'}
              </h3>
              <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">Displayed in the Legal Radar UI.</p>
            </div>
            {editingTopic && (
              <button className="text-xs text-luna-secondary hover:underline" onClick={resetTopicForm}>
                Cancel
              </button>
            )}
          </div>
          <form className="space-y-3" onSubmit={handleTopicSubmit}>
            <div>
              <label htmlFor="topic-key" className="text-xs font-semibold text-luna-text-light/70 dark:text-luna-text-dark/70">Key</label>
              <input
                id="topic-key"
                type="text"
                className="w-full rounded-xl border border-luna-secondary/30 bg-transparent px-3 py-2 text-sm"
                value={topicForm.key}
                onChange={(e) => setTopicForm({ ...topicForm, key: e.target.value })}
                disabled={!!editingTopic}
                required
              />
            </div>
            <div>
              <label htmlFor="topic-label" className="text-xs font-semibold text-luna-text-light/70 dark:text-luna-text-dark/70">Label</label>
              <input
                id="topic-label"
                type="text"
                className="w-full rounded-xl border border-luna-secondary/30 bg-transparent px-3 py-2 text-sm"
                value={topicForm.label}
                onChange={(e) => setTopicForm({ ...topicForm, label: e.target.value })}
                required
              />
            </div>
            <div>
              <label htmlFor="topic-description" className="text-xs font-semibold text-luna-text-light/70 dark:text-luna-text-dark/70">Description</label>
              <textarea
                id="topic-description"
                className="w-full rounded-xl border border-luna-secondary/30 bg-transparent px-3 py-2 text-sm"
                rows={3}
                value={topicForm.description}
                onChange={(e) => setTopicForm({ ...topicForm, description: e.target.value })}
              />
            </div>
            <label htmlFor="topic-active" className="flex items-center gap-2 text-sm text-luna-text-light dark:text-luna-text-dark">
              <input
                id="topic-active"
                type="checkbox"
                checked={topicForm.is_active}
                onChange={(e) => setTopicForm({ ...topicForm, is_active: e.target.checked })}
              />
              Topic is visible to end users
            </label>
            <button
              type="submit"
              className="w-full rounded-xl bg-luna-secondary px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
              disabled={savingTopic}
            >
              {savingTopic ? 'Saving…' : editingTopic ? 'Update topic' : 'Add topic'}
            </button>
          </form>
        </div>
        <div className="rounded-2xl border border-luna-secondary/20 bg-white dark:bg-luna-primary/40 shadow-sm p-4">
          <h3 className="text-lg font-bold text-luna-text-light dark:text-white mb-4">Latest consent log</h3>
          <ul className="space-y-3 text-sm text-luna-text-light dark:text-luna-text-dark max-h-72 overflow-y-auto pr-1">
            {consentLog.map((entry) => (
              <li key={entry.id} className="rounded-xl border border-luna-secondary/20 p-3">
                <div className="flex items-center justify-between text-xs text-luna-text-light/60 dark:text-luna-text-dark/60 mb-1">
                  <span>{entry.event_type}</span>
                  <span>{formatDistanceToNow(new Date(entry.created_at), { addSuffix: true })}</span>
                </div>
                <div className="text-xs font-semibold">User ID: {entry.user_id}</div>
              </li>
            ))}
            {consentLog.length === 0 && (
              <li className="text-sm text-luna-text-light/60 dark:text-luna-text-dark/60">No consent events recorded.</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    if (loading && !refreshing) {
      return (
        <div className="rounded-2xl border border-luna-secondary/20 bg-white/60 dark:bg-luna-primary/40 p-8 text-center text-sm text-luna-text-light/60 dark:text-luna-text-dark/60">
          Loading Legal Radar data…
        </div>
      );
    }

    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'sources':
        return renderSources();
      case 'documents':
        return renderDocuments();
      case 'updates':
        return renderUpdates();
      case 'preferences':
        return renderPreferences();
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-luna-text-light/60 dark:text-luna-text-dark/60">
            Legal Radar CMS
          </p>
          <h1 className="text-3xl font-black text-luna-text-light dark:text-white">Compliance content control</h1>
          <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 max-w-2xl">
            Manage licensed sources, ingestion, AI summaries, and notification preferences from one workspace.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={refreshAll}
            className="inline-flex items-center gap-2 rounded-full border border-luna-secondary/30 px-4 py-2 text-xs font-semibold text-luna-text-light dark:text-luna-text-dark disabled:opacity-60"
            disabled={refreshing}
          >
            <span className={`material-symbols-outlined text-base ${refreshing ? 'animate-spin' : ''}`}>refresh</span>
            Refresh data
          </button>
          <button
            onClick={() => invokeEdgeFunction('ingest-legal-sources')}
            className="inline-flex items-center gap-2 rounded-full border border-luna-secondary/30 px-4 py-2 text-xs font-semibold text-luna-text-light dark:text-luna-text-dark disabled:opacity-60"
            disabled={functionRunning === 'ingest-legal-sources'}
          >
            <span className="material-symbols-outlined text-base">auto_mode</span>
            Ingest now
          </button>
          <button
            onClick={() => invokeEdgeFunction('generate-legal-updates')}
            className="inline-flex items-center gap-2 rounded-full bg-luna-secondary px-4 py-2 text-xs font-semibold text-white disabled:opacity-60"
            disabled={functionRunning === 'generate-legal-updates'}
          >
            <span className="material-symbols-outlined text-base">spark</span>
            Generate summaries
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {tabConfig.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-semibold transition ${
              activeTab === tab.id
                ? 'border-luna-secondary bg-luna-secondary text-white'
                : 'border-luna-secondary/30 text-luna-text-light dark:text-luna-text-dark hover:border-luna-secondary/60'
            }`}
          >
            <span className="material-symbols-outlined text-base">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {renderContent()}
    </div>
  );
};
